import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BadgeCheck, Clock, CreditCard, Loader2, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Text } from '@/components/flup';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';
import { getVenueMonetisation } from '@/store/thunkMidddleware/monetisationMiddleware';
import { setPlanVenue } from '@/store/slices/monetisationSlice';
import { getCompletedVenueDetails } from '@/store/thunkMidddleware/turfMiddleware';
import { usePlanSwitcher } from '@/hooks/usePlanSwitcher';
import {
  VenueMonetisation,
  formatPaise,
  isAwaitingPayment,
  isPlanChangeLocked,
  isZeroCommission,
  planLabel,
} from '@/types/monetisationTypes';
import VenuePlansModal from '@/components/turf/VenuePlansModal';

/**
 * The current plan, always in the sidebar.
 *
 * Billing is per venue, so "the vendor's plan" does not exist — a vendor with
 * four venues has four plans that can differ. The card therefore only ever
 * names one plan when it can point at one venue: the venue in the route, or the
 * vendor's only venue. With several venues it shows the spread and hands off to
 * a list where each venue is switched on its own.
 */

/** `/dashboard/venueManagement/:id` and `/dashboard/editVenue/:id` carry a venue. */
const VENUE_ROUTES = [/\/dashboard\/venueManagement\/([^/]+)/, /\/dashboard\/editVenue\/([^/]+)/];

const venueIdFromPath = (pathname: string): string | null => {
  for (const pattern of VENUE_ROUTES) {
    const match = pathname.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
};

interface SidebarPlanCardProps {
  collapsed: boolean;
}

const SidebarPlanCard: React.FC<SidebarPlanCardProps> = ({ collapsed }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const routeVenueId = venueIdFromPath(location.pathname);

  const { venuePlans, venuePlanResponse, selectedVenueId, selectedVenueName } = useAppSelector(
    (state: RootState) => state.monetisation,
  );
  const { completedVenueDetails, pendingVenueDetails } = useAppSelector(
    (state: RootState) => state.turf,
  );

  const knownVenues: any[] = [
    ...(completedVenueDetails?.payload || []),
    ...(pendingVenueDetails?.payload || []),
  ];

  // Only venues that have gone live have a plan. Reading a plan mints the
  // record server-side, so venues that have not paid are left alone.
  const liveVenues = knownVenues.filter((v) => v?.oneTimeOnboardingFeeDetails?.isPaymentdone);
  const multipleVenues = liveVenues.length > 1;

  // One venue can be named honestly: the route's, or the only one there is.
  // Otherwise the card refuses to pick a venue on the vendor's behalf.
  const soleVenue = !multipleVenues ? liveVenues[0] || knownVenues[0] : undefined;
  const venueId = routeVenueId || soleVenue?._id || null;
  const venueName =
    (routeVenueId
      ? knownVenues.find((v) => v?._id === routeVenueId)?.name ||
        (selectedVenueId === routeVenueId ? selectedVenueName : undefined)
      : soleVenue?.name) || undefined;

  const current: VenueMonetisation | undefined = venueId ? venuePlans[venueId] : undefined;

  const [plansOpen, setPlansOpen] = useState(false);
  const askedForVenues = useRef(false);

  const { openSwitcher, handleSubscriptionCheckout, verifying, modals } = usePlanSwitcher(
    (id) => knownVenues.find((v) => v?._id === id)?.name,
  );

  // Landing straight on a non-venue route leaves nothing to describe, so the
  // venue list is pulled once to find one.
  useEffect(() => {
    if (venueId || askedForVenues.current || knownVenues.length) return;
    askedForVenues.current = true;
    dispatch(getCompletedVenueDetails({}));
  }, [venueId, knownVenues.length]);

  useEffect(() => {
    if (!venueId) return;
    dispatch(getVenueMonetisation({ venueId }));
    dispatch(setPlanVenue({ venueId, venueName }));
  }, [venueId, venueName]);

  const plansModal = (
    <VenuePlansModal
      open={plansOpen}
      onOpenChange={setPlansOpen}
      venues={knownVenues}
      onSubscriptionCheckout={handleSubscriptionCheckout}
    />
  );

  // No venue the card can speak for: either the vendor has none, or has several
  // and the route is not on one of them. Naming a plan here would be a guess.
  if (!venueId) {
    const nothingToShow = !knownVenues.length;

    if (collapsed) {
      return nothingToShow ? null : (
        <>
          <button
            type="button"
            onClick={() => setPlansOpen(true)}
            title="Plans"
            className="grid place-items-center w-10 h-10 mx-auto mb-2 rounded-flup-sm text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
          >
            <CreditCard className="w-[18px] h-[18px]" aria-hidden />
          </button>
          {plansModal}
          {modals}
        </>
      );
    }

    return (
      <>
        <div className="rounded-flup-sm border border-flup-hairline p-3 mb-2">
          <Text variant="eyebrow" tone="tertiary" as="p" className="mb-1">
            {nothingToShow ? 'Current plan' : 'Plans'}
          </Text>

          {nothingToShow ? (
            <Text variant="micro" tone="tertiary" as="p">
              Add a venue to choose a plan
            </Text>
          ) : (
            <>
              <Text variant="micro" tone="tertiary" as="p">
                {liveVenues.length} venues, each billed separately
              </Text>
              <button
                type="button"
                onClick={() => setPlansOpen(true)}
                className="mt-2.5 w-full h-8 rounded-flup-xs border border-flup-hairline-2 text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
              >
                <Text variant="caption" tone="inherit" as="span">
                  Manage plans
                </Text>
              </button>
              {plansModal}
            </>
          )}
        </div>
        {modals}
      </>
    );
  }

  const loading = venuePlanResponse?.isLoading && !current;
  const onSubscription = current?.plan === 'SUBSCRIPTION';
  const awaitingPayment = isAwaitingPayment(current);
  const pending = awaitingPayment ? undefined : current?.pendingPlanChange;
  // A granted plan (founding partner) cannot be switched at all, so the card
  // opens the same dialog read-only rather than inviting a switch.
  const locked = isPlanChangeLocked(current);
  const zeroCommission = isZeroCommission(current?.plan);
  const founding = current?.foundingPartner;
  // A granted plan has no mandate to break — only the paid subscription does.
  const mandateBroken =
    onSubscription && !locked && current?.mandate?.status && current.mandate.status !== 'ACTIVE';

  // Collapsed is an icon rail; a plan summary does not fit, so it becomes one
  // button that opens the same dialog.
  if (collapsed) {
    return (
      <>
        <button
          type="button"
          // The rail has no room to say which venue this is, so with several
          // venues it opens the list rather than one venue's switcher.
          onClick={() =>
            multipleVenues ? setPlansOpen(true) : openSwitcher(venueId, venueName)
          }
          title={multipleVenues ? 'Plans' : `${planLabel(current?.plan)} plan`}
          className="grid place-items-center w-10 h-10 mx-auto mb-2 rounded-flup-sm text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
        >
          <CreditCard className="w-[18px] h-[18px]" aria-hidden />
        </button>
        {plansModal}
        {modals}
      </>
    );
  }

  return (
    <>
      <div className="rounded-flup-sm border border-flup-hairline p-3 mb-2">
        <div className="flex items-center gap-2 mb-2">
          <Text variant="eyebrow" tone="tertiary" as="p">
            Current plan
          </Text>
          {venueName ? (
            <Text variant="micro" tone="tertiary" as="p" lines={1} className="min-w-0">
              · {venueName}
            </Text>
          ) : null}
          {verifying ? (
            <Loader2 className="w-3 h-3 animate-spin text-flup-brand ml-auto" aria-hidden />
          ) : null}
        </div>

        {loading ? (
          <Text variant="caption" tone="tertiary" as="p">
            Loading…
          </Text>
        ) : !current ? (
          <Text variant="caption" tone="tertiary" as="p">
            Plan unavailable
          </Text>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <BadgeCheck
                className={cn(
                  'w-4 h-4 shrink-0',
                  locked ? 'text-flup-brand' : zeroCommission ? 'text-flup-green' : 'text-flup-ink-3',
                )}
                aria-hidden
              />
              <Text variant="labelSemi" as="p" lines={1}>
                {planLabel(current.plan)}
              </Text>
            </div>

            <Text variant="micro" tone="tertiary" as="p" className="mt-1">
              {locked
                ? `0% commission${
                    typeof (founding?.daysRemaining ?? current.switchEligibility?.daysRemaining) ===
                    'number'
                      ? ` · ${
                          founding?.daysRemaining ?? current.switchEligibility?.daysRemaining
                        } days left`
                      : ''
                  }`
                : onSubscription
                  ? `${formatPaise(current.payablePaise)} · ${current.billingCycle ?? ''}`
                  : 'Commission per booking'}
            </Text>

            {awaitingPayment ? (
              <div className="flex items-start gap-1.5 mt-2">
                <Clock className="w-3.5 h-3.5 text-flup-warning shrink-0 mt-0.5" aria-hidden />
                <Text variant="micro" tone="tertiary" as="p">
                  Payment pending for zero commission
                </Text>
              </div>
            ) : null}

            {pending ? (
              <div className="flex items-start gap-1.5 mt-2">
                <Clock className="w-3.5 h-3.5 text-flup-warning shrink-0 mt-0.5" aria-hidden />
                <Text variant="micro" tone="tertiary" as="p">
                  Switching to{' '}
                  {pending.toPlan === 'COMMISSION' ? 'pay per booking' : 'zero commission'}
                </Text>
              </div>
            ) : null}

            {mandateBroken && !pending ? (
              <div className="flex items-start gap-1.5 mt-2">
                <TriangleAlert
                  className="w-3.5 h-3.5 text-flup-warning shrink-0 mt-0.5"
                  aria-hidden
                />
                <Text variant="micro" tone="tertiary" as="p">
                  Auto-debit needs re-authorising
                </Text>
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => openSwitcher(venueId, venueName)}
              className="mt-2.5 w-full h-8 rounded-flup-xs border border-flup-hairline-2 text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
            >
              <Text variant="caption" tone="inherit" as="span">
                {locked
                  ? 'View plan'
                  : awaitingPayment
                    ? 'Complete payment'
                    : pending
                      ? 'Manage plan'
                      : 'Switch plan'}
              </Text>
            </button>

            {multipleVenues ? (
              <button
                type="button"
                onClick={() => setPlansOpen(true)}
                className="mt-1.5 w-full text-flup-ink-3 hover:text-flup-ink transition-colors"
              >
                <Text variant="micro" tone="inherit" as="span">
                  All {liveVenues.length} venue plans
                </Text>
              </button>
            ) : null}
          </>
        )}
      </div>

      {plansModal}
      {modals}
    </>
  );
};

export default SidebarPlanCard;
